<template>
  <div>
    <v-text-field
      v-model="cmpValue"
      :label="label"
      :placeholder="placeholder"
      :readonly="readonly"
      :disabled="disabled"
      :outlined="outlined"
      :dense="dense"
      :hide-details="hideDetails"
      :error="error"
      :error-messages="errorMessages"
      :rules="rules"
      :clearable="clearable"
      :background-color="backgroundColor"
      :prefix="options.prefix"
      :suffix="options.suffix"
      v-bind="properties"
      @keypress="keyPress"
      @blur="onBlur"
      @change="$emit('change', $event)"
      @keyup="$emit('keyup', $event)"
    ></v-text-field>
  </div>
</template>

<script>
export default {
  model: { prop: 'value', event: 'input' },
  props: {
    value: {
      type: [String, Number],
      default: '0',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [Boolean, String],
      default: false,
    },
    errorMessages: {
      type: [Array, String],
      default: () => [],
    },
    rules: {
      type: [Array, String],
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    valueWhenIsEmpty: {
      type: String,
      default: '',
    },
    allowNegative: {
      type: Boolean,
      default: false,
    },
    valueOptions: {
      type: Object,
      default() {
        return {
          min: 0,
          minEvent: 'SetValueMin',
        }
      },
    },
    options: {
      type: Object,
      default() {
        return {
          locale: 'pt-BR',
          prefix: '',
          suffix: '',
          length: 11,
          precision: 2,
        }
      },
    },
    properties: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  computed: {
    cmpValue: {
      get() {
        return this.value !== null && this.value !== ''
          ? this.humanFormat(this.value.toString())
          : this.valueWhenIsEmpty
      },
      set(newValue) {
        this.$emit('input', this.machineFormat(newValue))
      },
    },
  },
  methods: {
    humanFormat(number) {
      if (isNaN(number)) {
        return ''
      }

      return Number(number).toLocaleString(this.options.locale, {
        maximumFractionDigits: this.options.precision,
        minimumFractionDigits: this.options.precision,
      })
    },
    machineFormat(number) {
      if (!number) {
        return this.valueWhenIsEmpty
      }

      const hasNegativeSign = this.allowNegative && number.toString().trim().startsWith('-')
      let normalized = this.cleanNumber(number)

      normalized = normalized.padStart(parseInt(this.options.precision, 10) + 1, '0')
      normalized = `${normalized.substring(0, normalized.length - parseInt(this.options.precision, 10))
      }.${
        normalized.substring(normalized.length - parseInt(this.options.precision, 10), normalized.length)}`

      if (this.options.precision === 0) {
        normalized = this.cleanNumber(normalized)
      }

      if (isNaN(normalized)) {
        return this.valueWhenIsEmpty
      }

      const output = hasNegativeSign ? `-${normalized}` : normalized

      return output
    },
    keyPress($event) {
      const keyCode = $event.keyCode ? $event.keyCode : $event.which
      const inputValue = ($event.target && $event.target.value) || ''
      const cursorPos = ($event.target && $event.target.selectionStart) || 0

      if (this.allowNegative && keyCode === 45) {
        const hasMinus = inputValue.includes('-')
        if (hasMinus || cursorPos !== 0) {
          $event.preventDefault()
        }

        return
      }

      if (keyCode < 48 || keyCode > 57) {
        $event.preventDefault()
      }

      if (this.targetLength()) {
        $event.preventDefault()
      }
    },
    cleanNumber(value) {
      let result = ''
      if (!value) {
        return result
      }

      let flag = false
      const arrayValue = value.toString().split('')
      for (let i = 0; i < arrayValue.length; i += 1) {
        if (this.isInteger(arrayValue[i])) {
          if (!flag) {
            if (arrayValue[i] !== '0') {
              result += arrayValue[i]
              flag = true
            }
          } else {
            result += arrayValue[i]
          }
        }
      }

      return result
    },
    isInteger(value) {
      return Number.isInteger(parseInt(value, 10))
    },
    targetLength() {
      return Number(this.cleanNumber(this.value).length) >= Number(this.options.length)
    },
    onBlur() {
      if (this.value.length === 0 || parseFloat(this.value) <= this.valueOptions.min) {
        this.$emit(this.valueOptions.minEvent || 'SetValueMin', this.valueOptions.min)
      }

      if (this.valueOptions.max && parseFloat(this.value) >= this.valueOptions.max) {
        this.$emit(this.valueOptions.maxEvent || 'SetValueMax', this.valueOptions.max)
      }
    },
  },
}
</script>
